import PolicyLayout, { PolicySection } from "@/components/PolicyLayout";

const LAST_UPDATED = "May 15, 2026";

export default function ShippingPolicyPage() {
  return (
    <PolicyLayout
      title="Shipping Policy"
      subtitle="Delivery"
      lastUpdated={LAST_UPDATED}
      testId="shipping-policy-page"
    >
      <p>
        Resurrected Gun Parts ships exclusively within the <strong>Continental United States</strong>.
        We do not ship to Alaska, Hawaii, the District of Columbia, any U.S. territory, APO/FPO/DPO box,
        freight forwarder, or any international destination. We also do not ship to any state, county,
        municipality, or address where the sale, possession, or use of the ordered product is restricted or
        prohibited by federal, state, or local law. This policy outlines our handling times, available shipping
        methods, and what to expect after your order is placed.
      </p>

      <PolicySection title="Processing Time">
        <p>
          Most orders ship within <strong className="text-white">48 hours</strong> of payment confirmation. Orders
          placed on weekends or federal holidays will be processed on the next business day. If we anticipate any
          delay (for example, a temporarily out-of-stock item, a custom-fit run, or a personal scheduling matter), we
          will contact you directly by email or phone before processing your order.
        </p>
      </PolicySection>

      <PolicySection title="Shipping Methods">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong className="text-white">USPS Priority Mail</strong> — our standard shipping method for most parts.
            Typical transit time is 2–3 business days within the continental U.S. Includes tracking and up to $100 of
            insurance.
          </li>
          <li>
            <strong className="text-white">UPS Ground or USPS Ground Advantage</strong> — used for oversized,
            heavyweight, or otherwise restricted items. Typical transit time is 3–7 business days depending on
            destination within the continental U.S. Tracking included.
          </li>
        </ul>
        <p>
          The appropriate carrier and service level is determined at checkout based on item dimensions, weight, and
          destination. Custom shipping rates may apply for select heavy or oversized items as noted on the product
          page.
        </p>
      </PolicySection>

      <PolicySection title="Shipping Destinations &amp; Restrictions">
        <p>
          <strong className="text-white">Continental United States only.</strong> We ship only to the 48
          contiguous U.S. states. We do not ship to <strong className="text-white">Alaska, Hawaii, or the
          District of Columbia</strong>, and we do not ship internationally, to U.S. territories,
          APO/FPO/DPO addresses, or via freight forwarders. Orders placed for delivery to any excluded
          destination will be cancelled and refunded.
        </p>
        <p>
          <strong className="text-white">No shipments to prohibited jurisdictions.</strong> We do not ship any
          product to any address where the sale, possession, transfer, or use of that product is restricted or
          prohibited by federal, state, county, or municipal law. You are solely responsible for verifying that the
          item you order is legal at your shipping address. If we determine an order would violate applicable law,
          we will cancel and refund the order at our sole discretion.
        </p>
      </PolicySection>

      <PolicySection title="Shipping Rates">
        <p>
          A flat shipping rate is applied to most orders and displayed in the cart and at checkout before payment.
          Larger or heavier items may carry a custom shipping rate set on the product listing.
        </p>
      </PolicySection>

      <PolicySection title="Tracking">
        <p>
          You will receive a tracking number by email as soon as your label is generated. Please allow up to 24 hours
          for tracking events to appear on the carrier's website after the label prints.
        </p>
      </PolicySection>

      <PolicySection title="Shipping Address Accuracy">
        <p>
          You are responsible for providing a complete and accurate shipping address at checkout. If a package is
          returned to us as undeliverable due to an incorrect or incomplete address, we will contact you and re-ship
          the order at your expense. Address changes can be made only before the order has been packed and labeled.
        </p>
      </PolicySection>

      <PolicySection title="Lost or Delayed Packages">
        <p>
          Once your package is in the hands of the carrier, delivery timelines are outside our control. If your
          tracking shows no movement for more than 7 business days or your package is marked delivered but missing,
          please contact us within 14 days of the expected delivery date so we can help open a carrier investigation
          or insurance claim.
        </p>
      </PolicySection>

      <PolicySection title="Damaged in Transit">
        <p>
          If your package arrives damaged, photograph the packaging and contents <em>before</em> unboxing further and
          email us within 48 hours of delivery. See our{" "}
          <a href="/refund-policy" className="text-[#C0A062] hover:underline">Refund Policy</a> for full details on
          how damage claims are handled.
        </p>
      </PolicySection>

      <PolicySection title="Questions">
        <p>
          Email{" "}
          <a href="mailto:admin@resurrectedgunparts.com" className="text-[#C0A062] hover:underline">
            admin@resurrectedgunparts.com
          </a>{" "}
          or call (928) 637-8133.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
